import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, X } from "lucide-react";
import { api } from "@/lib/api";

// Pop-up « Envoie tes documents » : à l'ouverture du tableau de bord si les
// documents manquent, et à chaque action bloquée par le serveur.
export default function DocumentsRequiredPopup({ role }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const later = sessionStorage.getItem("price_docs_popup_later") === "1";
    api.get("/price/identity/status")
      .then(({ data }) => {
        setInfo(data);
        if (data?.docs_ok) return;
        if (role === "driver") {
          // Conducteur : l'envoi des documents est une étape obligatoire.
          api.post("/price/driver/online", { online: false }).catch(() => {});
          nav("/verify-identity?signup=1", { replace: true });
          return;
        }
        if (!later) setOpen(true);
        if ((data?.role || role) === "driver") {
          api.post("/price/driver/online", { online: false }).catch(() => {});
          window.dispatchEvent(new Event("price:force-offline"));
        }
      })
      .catch(() => {});
    const on = () => setOpen(true);
    window.addEventListener("price:documents-required", on);
    return () => window.removeEventListener("price:documents-required", on);
  }, []);

  if (!open) return null;
  const dismiss = () => { sessionStorage.setItem("price_docs_popup_later", "1"); setOpen(false); };
  const isDriver = (info?.role || role) === "driver";
  const rejected = info?.status === "rejected";

  return (
    <div className="fixed inset-0 z-[150] flex items-end justify-center bg-black/50 p-4 sm:items-center animate-in fade-in duration-200" data-testid="documents-required-popup">
      <div className="relative w-full max-w-sm rounded-3xl bg-white p-6 street-border street-shadow animate-in slide-in-from-bottom-4 duration-300">
        <button type="button" onClick={dismiss} aria-label="Fermer"
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-[#FFF8F1] street-border-thin" data-testid="documents-required-close">
          <X className="h-4 w-4" strokeWidth={3} />
        </button>
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-accent street-border rotate-[-6deg]">
          <FileUp className="h-7 w-7" strokeWidth={2.5} />
        </div>
        <h2 className="mt-4 font-[Outfit] text-[24px] font-black leading-tight">
          {rejected ? "Documents à renvoyer" : "Envoie tes documents"}
        </h2>
        <p className="mt-2 text-[14px] font-medium text-foreground/75">
          {isDriver
            ? "Tant que tes documents ne sont pas envoyés, tu ne peux pas passer en ligne, accepter de course ni publier de trajet sur Price."
            : "Tant que tes documents ne sont pas envoyés, tu ne peux pas demander de course, programmer ni réserver de trajet sur Price."}
        </p>
        <p className="mt-2 text-[12px] font-bold text-foreground/60">
          {isDriver ? "Pièce d'identité, permis, assurance et carte grise." : "Pièce d'identité recto / verso."}
          {rejected && info?.rejection_reason ? ` Motif du refus : ${info.rejection_reason}` : ""}
        </p>
        <button type="button" onClick={() => { setOpen(false); nav("/verify-identity"); }}
          className="street-press mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-[Outfit] font-black text-primary-foreground street-border street-shadow-sm"
          data-testid="documents-required-send">
          <FileUp className="h-4 w-4" strokeWidth={2.75} />Envoyer mes documents
        </button>
        <button type="button" onClick={dismiss}
          className="mt-3 h-10 w-full text-[13px] font-black text-foreground/60" data-testid="documents-required-later">
          Plus tard
        </button>
      </div>
    </div>
  );
}
